import React, { useMemo } from 'react';
import { Eye, EyeOff, ChevronRight, ChevronDown, Lock, LockOpen, GripVertical, Trash2 } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { useLayer } from '../../context/LayerContext';
import { getAllLayersReversed, getLayerForObject } from '../../constants/layerDefinitions';
import { useCommand } from '../../context/CommandContext';
import { ReorderObjectCommand } from '../../commands/ReorderObjectCommand';
import { isUserGroup, enterEditMode } from './utils/groupUtils';
import ConfirmDialog from '../ui/ConfirmDialog';

const HEIGHT_ZONE_TYPES = new Set(['height-zone', 'height-zone-label']);

const isHeightZoneObject = (obj) => HEIGHT_ZONE_TYPES.has(obj?.data?.type);

const partitionHeightZones = (objects) => {
  const heightObjects = [];
  const otherObjects = [];
  objects.forEach((obj) => {
    if (isHeightZoneObject(obj)) {
      heightObjects.push(obj);
    } else {
      otherObjects.push(obj);
    }
  });
  return { heightObjects, otherObjects };
};

/**
 * SortableObjectItem - A single object row within a layer that supports drag reordering.
 * Defined outside LayerPanel to avoid re-creation on each parent render.
 */
const SortableObjectItem = ({
  obj,
  showDragHandle,
  isSelected,
  isHidden,
  isLocked,
  displayName,
  onSelect,
  onToggleVisibility,
  onToggleLock,
  onRemove,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: obj.__uniqueId,
    disabled: !showDragHandle,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    minHeight: '36px',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center w-full ${showDragHandle ? 'pl-4' : 'pl-8'} pr-3 transition-colors ${
        isSelected ? 'bg-emerald-50 text-emerald-700' : 'text-gray-600 hover:bg-gray-50'
      }`}
      role="listitem"
      data-testid="object-row"
    >
      {showDragHandle && (
        <button
          {...attributes}
          {...listeners}
          className="p-1 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing flex-shrink-0"
          aria-label={`${displayName} ziehen um Reihenfolge zu ändern`}
          title="Ziehen zum Verschieben"
          tabIndex={-1}
        >
          <GripVertical size={14} />
        </button>
      )}

      {/* Object name - clickable to select */}
      <button
        onClick={() => onSelect(obj)}
        className="flex-1 text-left truncate"
        aria-label={`${displayName} auswählen`}
        title={displayName}
      >
        <span className="text-xs truncate">{displayName}</span>
      </button>

      {/* Visibility toggle for individual object */}
      <button
        onClick={(e) => { e.stopPropagation(); onToggleVisibility(obj); }}
        className={`p-1 rounded transition-colors flex-shrink-0 ${
          !isHidden ? 'text-gray-600 hover:text-gray-900' : 'text-gray-300 hover:text-gray-500'
        }`}
        aria-label={`${displayName} ${isHidden ? 'einblenden' : 'ausblenden'}`}
        title={isHidden ? `${displayName} einblenden` : `${displayName} ausblenden`}
      >
        {isHidden ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>

      {/* Lock toggle for individual object */}
      <button
        onClick={(e) => { e.stopPropagation(); onToggleLock(obj); }}
        className={`p-1 rounded transition-colors flex-shrink-0 ${
          isLocked ? 'text-amber-500 hover:text-amber-700' : 'text-gray-300 hover:text-gray-500'
        }`}
        aria-label={`${displayName} ${isLocked ? 'entsperren' : 'sperren'}`}
        title={isLocked ? `${displayName} entsperren` : `${displayName} sperren`}
      >
        {isLocked ? <Lock size={14} /> : <LockOpen size={14} />}
      </button>

      {/* Remove action — only wired up for removable objects (the wizard
          background image, #766). Other rows never receive onRemove. */}
      {onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(obj); }}
          className="p-1 rounded transition-colors flex-shrink-0 text-gray-300 hover:text-red-600"
          aria-label={`${displayName} entfernen`}
          title={`${displayName} entfernen`}
        >
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
};

/**
 * GroupSubItem - Read-only row for a sub-object inside an expanded group.
 * Click selects the parent group in edit mode and activates the sub-object.
 */
const GroupSubItem = ({ subObj, parentGroup, canvas, isHidden, onToggleVisibility, getDisplayName }) => {
  const displayName = getDisplayName ? getDisplayName(subObj) : (subObj.data?.label || subObj.objectType || 'Objekt');

  const handleClick = () => {
    if (!canvas || !parentGroup) return;
    enterEditMode(canvas, parentGroup);
    canvas.setActiveObject(subObj);
    canvas.renderAll();
  };

  return (
    <div
      className="flex items-center w-full pl-12 pr-3 text-gray-500 hover:bg-gray-50 transition-colors"
      style={{ minHeight: '30px' }}
    >
      <button
        onClick={handleClick}
        className="flex-1 text-left truncate"
        title={displayName}
      >
        <span className="text-xs truncate italic">{displayName}</span>
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onToggleVisibility(subObj); }}
        className={`p-1 rounded transition-colors flex-shrink-0 ${
          !isHidden ? 'text-gray-400 hover:text-gray-600' : 'text-gray-300 hover:text-gray-500'
        }`}
        aria-label={isHidden ? 'Einblenden' : 'Ausblenden'}
      >
        {isHidden ? <EyeOff size={12} /> : <Eye size={12} />}
      </button>
    </div>
  );
};

/**
 * GroupObjectItem - Sortable row for a user-created Group.
 * Shows a chevron toggle to expand/collapse sub-objects.
 */
const GroupObjectItem = ({
  obj,
  showDragHandle,
  isSelected,
  isHidden,
  isLocked,
  onSelect,
  onToggleVisibility,
  onToggleLock,
  canvas,
  getDisplayName,
  isObjectHidden,
  onToggleSubVisibility,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const subObjects = obj.getObjects ? obj.getObjects() : [];
  const subCount = subObjects.length;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: obj.__uniqueId,
    disabled: !showDragHandle,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    minHeight: '36px',
  };

  return (
    <div ref={setNodeRef} style={{ transform: style.transform, transition: style.transition, opacity: style.opacity }}>
      {/* Group row */}
      <div
        className={`flex items-center w-full ${showDragHandle ? 'pl-4' : 'pl-6'} pr-3 transition-colors ${
          isSelected ? 'bg-emerald-50 text-emerald-700' : 'text-gray-600 hover:bg-gray-50'
        }`}
        style={{ minHeight: '36px' }}
        role="listitem"
      >
        {showDragHandle && (
          <button
            {...attributes}
            {...listeners}
            className="p-1 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing flex-shrink-0"
            aria-label="Gruppe ziehen um Reihenfolge zu ändern"
            title="Ziehen zum Verschieben"
            tabIndex={-1}
          >
            <GripVertical size={14} />
          </button>
        )}

        {/* Chevron to expand/collapse sub-objects */}
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded(prev => !prev); }}
          className="p-0.5 text-gray-400 hover:text-gray-600 flex-shrink-0"
          aria-label={expanded ? 'Gruppe zuklappen' : 'Gruppe aufklappen'}
          title={expanded ? 'Zuklappen' : 'Aufklappen'}
        >
          {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </button>

        {/* Group name */}
        <button
          onClick={() => onSelect(obj)}
          className="flex-1 text-left truncate ml-0.5"
          aria-label={`Gruppe (${subCount} Objekte) auswählen`}
          title={`Gruppe (${subCount} Objekte)`}
        >
          <span className="text-xs truncate font-medium">Gruppe ({subCount})</span>
        </button>

        {/* Visibility */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleVisibility(obj); }}
          className={`p-1 rounded transition-colors flex-shrink-0 ${
            !isHidden ? 'text-gray-600 hover:text-gray-900' : 'text-gray-300 hover:text-gray-500'
          }`}
          aria-label={isHidden ? 'Gruppe einblenden' : 'Gruppe ausblenden'}
          title={isHidden ? 'Gruppe einblenden' : 'Gruppe ausblenden'}
        >
          {isHidden ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>

        {/* Lock */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleLock(obj); }}
          className={`p-1 rounded transition-colors flex-shrink-0 ${
            isLocked ? 'text-amber-500 hover:text-amber-700' : 'text-gray-300 hover:text-gray-500'
          }`}
          aria-label={isLocked ? 'Gruppe entsperren' : 'Gruppe sperren'}
          title={isLocked ? 'Gruppe entsperren' : 'Gruppe sperren'}
        >
          {isLocked ? <Lock size={14} /> : <LockOpen size={14} />}
        </button>
      </div>

      {/* Sub-objects (shown when expanded) */}
      {expanded && subObjects.map((subObj, i) => (
        <GroupSubItem
          key={subObj.__uniqueId || i}
          subObj={subObj}
          parentGroup={obj}
          canvas={canvas}
          getDisplayName={getDisplayName}
          isHidden={isObjectHidden(subObj)}
          onToggleVisibility={onToggleSubVisibility}
        />
      ))}
    </div>
  );
};

/**
 * HeightZoneCollectionItem - Single virtual row that bundles all height-zone
 * polygons + their labels into one entry in the Background layer. Toggling
 * the eye icon shows/hides every height polygon and label at once.
 */
const HeightZoneCollectionItem = ({
  heightObjects,
  isHidden,
  onToggleVisibility,
}) => {
  const zoneCount = heightObjects.filter(
    (obj) => obj.data?.type === 'height-zone'
  ).length;

  return (
    <div
      className="flex items-center w-full pl-8 pr-3 text-gray-600 hover:bg-gray-50 transition-colors"
      style={{ minHeight: '36px' }}
      role="listitem"
    >
      <span className="flex-1 text-left truncate">
        <span className="text-xs truncate font-medium">Höhenzonen ({zoneCount})</span>
      </span>

      <button
        onClick={(e) => { e.stopPropagation(); onToggleVisibility(heightObjects); }}
        className={`p-1 rounded transition-colors flex-shrink-0 ${
          !isHidden ? 'text-gray-600 hover:text-gray-900' : 'text-gray-300 hover:text-gray-500'
        }`}
        aria-label={isHidden ? 'Höhenzonen einblenden' : 'Höhenzonen ausblenden'}
        title={isHidden ? 'Höhenzonen einblenden' : 'Höhenzonen ausblenden'}
      >
        {isHidden ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
  );
};

/**
 * LayerPanel - Layer management panel for the Properties area
 *
 * Displays layers as a single-level collapsible structure where each layer
 * is a collapsible group showing objects when expanded.
 *
 * Displays layers in reverse order (highest Z-index on top, like Photoshop/Figma)
 *
 * Now used as tab content within the Properties panel, so it no longer has
 * its own collapsible header.
 *
 * Objects within each layer can be reordered via drag & drop (GripVertical handle).
 * Drag handles are only shown for unlocked layers with more than 1 object.
 */
const LayerPanel = ({ canvas, selectedObject }) => {
  const {
    toggleLayerVisibility,
    toggleLayerCollapsed,
    isLayerVisible,
    isLayerCollapsed,
    getObjectsPerLayer,
    getObjectDisplayName,
    layerVisibility,
    toggleLayerLock,
    isLayerLocked,
    layerLocked,
    // Per-object hide/lock lives in the context's objectViewState map (#760),
    // persisted per garden in settings — so it survives a panel unmount on the
    // Ebenen↔Eigenschaften tab switch and a page reload. objectViewState is the
    // apply-effect dependency; the helpers read/write it.
    objectViewState,
    toggleObjectHidden,
    isObjectHidden,
    toggleObjectLocked,
    isObjectLocked,
    toggleObjectsHidden,
    areObjectsHidden,
    // Removing the wizard background image (#766): clears settings.backgroundImage
    // — GardenCanvas's wizard-reload effect then drops the live canvas object.
    removeBackgroundImage,
  } = useLayer();

  const { commandStack } = useCommand();

  // The wizard background image is the one object the panel can delete (#766).
  // Held here until the confirmation dialog is answered.
  const [backgroundToRemove, setBackgroundToRemove] = React.useState(null);

  const confirmRemoveBackground = () => {
    removeBackgroundImage();
    setBackgroundToRemove(null);
  };

  // The hide/lock state itself is persisted by the context into
  // settings.objectViewState. For USER content the map key is the object's
  // data.objectId, which only survives a reload if the object (with that id) is
  // written to canvas_objects — so we also nudge a canvas save. This dedicated,
  // side-effect-free event is one GardenCanvas listens to for exactly that
  // (#760); it is harmless for wizard objects (keyed deterministically, not in
  // canvas_objects). Wrap each context toggle so flip + save travel together.
  const persistViewState = () => {
    canvas?.fire('object:viewstate:changed');
  };
  const handleToggleObjectHidden = (obj) => { toggleObjectHidden(obj); persistViewState(); };
  const handleToggleObjectLocked = (obj) => { toggleObjectLocked(obj); persistViewState(); };
  const handleToggleObjectsHidden = (objs) => { toggleObjectsHidden(objs); persistViewState(); };

  // Get layers in reverse order (highest Z-index first)
  const layers = useMemo(() => getAllLayersReversed(), []);

  // Force re-render counter when object properties change
  const [refreshCounter, setRefreshCounter] = React.useState(0);

  // DnD sensors – require 5px movement to activate (avoids accidental drags)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Listen for object modifications and layer reorders to update display names / order
  React.useEffect(() => {
    if (!canvas) return;

    const handleChange = () => {
      setRefreshCounter((prev) => prev + 1);
    };

    canvas.on('object:modified', handleChange);
    canvas.on('object:changed', handleChange);
    canvas.on('object:updated', handleChange);
    canvas.on('object:added', handleChange);
    canvas.on('object:removed', handleChange);
    canvas.on('layer:reordered', handleChange);

    // Poll every 2 seconds as a fallback (for property changes that don't trigger events)
    const pollInterval = setInterval(handleChange, 2000);

    return () => {
      canvas.off('object:modified', handleChange);
      canvas.off('object:changed', handleChange);
      canvas.off('object:updated', handleChange);
      canvas.off('object:added', handleChange);
      canvas.off('object:removed', handleChange);
      canvas.off('layer:reordered', handleChange);
      clearInterval(pollInterval);
    };
  }, [canvas]);

  // Get canvas object count for dependency tracking
  const canvasObjectCount = canvas?.getObjects()?.length || 0;

  // Get objects grouped by layer
  const objectsByLayer = useMemo(() => {
    return getObjectsPerLayer(canvas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas, getObjectsPerLayer, canvasObjectCount, refreshCounter]);

  // Apply layer and object visibility + lock to canvas objects
  React.useEffect(() => {
    if (!canvas) return;

    const objects = canvas.getObjects();
    let needsRender = false;

    objects.forEach((obj) => {
      // Skip grid lines and snap guides
      if (obj.data?.isGridLine || obj.data?.isSnapGuide) return;

      // Get the layer for this object
      const layerId = getLayerForObject(obj);
      if (!layerId) return;

      // --- Visibility ---
      const layerVisible = isLayerVisible(layerId);
      const objectHidden = isObjectHidden(obj);
      const shouldBeVisible = layerVisible && !objectHidden;

      if (obj.visible !== shouldBeVisible) {
        obj.visible = shouldBeVisible;
        needsRender = true;
      }

      // --- Lock ---
      const layerLock = isLayerLocked(layerId);
      const objectLock = isObjectLocked(obj);
      const shouldBeLocked = layerLock || objectLock;
      const isWizardLocked = obj.data?.locked === true;

      if (shouldBeLocked) {
        if (!obj.lockMovementX) {
          obj.lockMovementX = true;
          obj.lockMovementY = true;
          obj.lockRotation = true;
          obj.lockScalingX = true;
          obj.lockScalingY = true;
          obj.hasControls = false;
          obj.selectable = false;
          obj.evented = false;
          // Drop any live selection so a locked object doesn't keep its
          // handles until the next mouse event.
          if (canvas.getActiveObject() === obj) {
            canvas.discardActiveObject();
          }
          needsRender = true;
        }
      } else if (!isWizardLocked) {
        if (obj.lockMovementX) {
          obj.lockMovementX = false;
          obj.lockMovementY = false;
          obj.lockRotation = false;
          obj.lockScalingX = false;
          obj.lockScalingY = false;
          obj.hasControls = true;
          obj.selectable = true;
          obj.evented = true;
          needsRender = true;
        }
      }
    });

    if (needsRender) {
      canvas.renderAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isObjectHidden/isObjectLocked are recreated each render but read objectViewState, which is the real trigger
  }, [canvas, layerVisibility, layerLocked, objectViewState, refreshCounter, isLayerVisible, isLayerLocked]);

  // Handle object click - select on canvas
  const handleObjectClick = (fabricObject) => {
    if (!canvas || !fabricObject) return;
    canvas.discardActiveObject();
    canvas.setActiveObject(fabricObject);
    canvas.renderAll();
  };

  // Check if a fabric object is currently selected
  const isObjectSelected = (fabricObject) => {
    if (!selectedObject || !fabricObject) return false;
    return selectedObject === fabricObject;
  };

  /**
   * Handle drag end for a layer's object list.
   *
   * The panel shows objects in reversed order (highest zIndexWithinLayer = top).
   * After reordering, we map panel positions back to zIndexWithinLayer values:
   *   panel position 0 (top) → highest zIndex (n-1)
   *   panel position n-1 (bottom) → lowest zIndex (0)
   *
   * @param {Object[]} objects - Objects in canvas order (ascending zIndexWithinLayer)
   */
  const handleDragEnd = (objects) => ({ active, over }) => {
    if (!over || active.id === over.id || !canvas) return;

    // Panel displays objects reversed (highest zIndex first)
    const displayObjects = [...objects].reverse();
    const oldIndex = displayObjects.findIndex(o => o.__uniqueId === active.id);
    const newIndex = displayObjects.findIndex(o => o.__uniqueId === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newDisplayOrder = arrayMove(displayObjects, oldIndex, newIndex);
    const n = newDisplayOrder.length;

    const changes = newDisplayOrder.map((obj, panelIndex) => ({
      object: obj,
      oldZIndex: obj.data?.zIndexWithinLayer ?? 0,
      newZIndex: n - 1 - panelIndex,
    }));

    commandStack.execute(new ReorderObjectCommand(canvas, changes));
  };

  return (
    <div
      className="bg-gray-50"
      role="region"
      aria-label="Ebenen-Verwaltung"
    >
      {/* Layer List */}
      <div role="list" aria-label="Layer-Liste">
        {layers.map((layer) => {
            const allObjects = objectsByLayer[layer.id] || [];

            // For the Background layer, fold all height-zone polygons + labels
            // into one virtual collection row so the panel shows a single entry.
            const { heightObjects, otherObjects } = layer.id === 'background'
              ? partitionHeightZones(allObjects)
              : { heightObjects: [], otherObjects: allObjects };
            const hasHeightCollection = heightObjects.length > 0;

            const objects = otherObjects;
            const count = objects.length + (hasHeightCollection ? 1 : 0);
            const visible = isLayerVisible(layer.id);
            const locked = isLayerLocked(layer.id);
            const collapsed = isLayerCollapsed(layer.id);
            const isEmpty = count === 0;
            const showDragHandle = !locked && objects.length > 1;

            // Ensure all objects have a stable __uniqueId before rendering DnD
            objects.forEach(o => { if (!o.__uniqueId) o.__uniqueId = Math.random().toString(36); });

            // Display order: reversed so highest zIndexWithinLayer is at top (like Figma)
            const displayObjects = [...objects].reverse();
            const itemIds = displayObjects.map(o => o.__uniqueId);

            return (
              <div key={layer.id} role="listitem">
                {/* Layer Header Row */}
                <div
                  className="flex items-center gap-2 px-3 hover:bg-gray-100 transition-colors group"
                  style={{ minHeight: '44px' }}
                  data-testid="layer-row"
                >
                  {/* Chevron toggle (or placeholder for empty layers) */}
                  <button
                    onClick={() => !isEmpty && toggleLayerCollapsed(layer.id)}
                    disabled={isEmpty}
                    className={`p-0.5 rounded transition-colors ${
                      isEmpty
                        ? 'text-gray-300 cursor-default'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    aria-expanded={!collapsed && !isEmpty}
                    aria-label={`${layer.name} ${collapsed ? 'aufklappen' : 'zuklappen'}`}
                  >
                    {collapsed || isEmpty ? (
                      <ChevronRight size={14} />
                    ) : (
                      <ChevronDown size={14} />
                    )}
                  </button>

                  {/* Color indicator */}
                  <span
                    className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: layer.color }}
                    aria-hidden="true"
                    data-testid="layer-color-indicator"
                  />

                  {/* Layer name */}
                  <span
                    className={`text-[13px] flex-1 truncate ${
                      isEmpty ? 'text-gray-400' : locked ? 'text-gray-500 italic' : 'text-gray-700'
                    }`}
                  >
                    {layer.name}
                  </span>

                  {/* Object count */}
                  <span className="text-xs text-gray-400 mr-1">
                    ({count})
                  </span>

                  {/* Visibility toggle */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleLayerVisibility(layer.id);
                    }}
                    className={`p-1 rounded transition-colors ${
                      visible
                        ? 'text-gray-600 hover:text-gray-900'
                        : 'text-gray-300 hover:text-gray-500'
                    }`}
                    aria-label={`${layer.name} ${visible ? 'ausblenden' : 'einblenden'}`}
                    title={visible ? `${layer.name} ausblenden` : `${layer.name} einblenden`}
                  >
                    {visible ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>

                  {/* Lock toggle */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleLayerLock(layer.id);
                    }}
                    className={`p-1 rounded transition-colors ${
                      locked
                        ? 'text-amber-500 hover:text-amber-700'
                        : 'text-gray-300 hover:text-gray-500'
                    }`}
                    aria-label={`${layer.name} ${locked ? 'entsperren' : 'sperren'}`}
                    title={locked ? `${layer.name} entsperren` : `${layer.name} sperren`}
                  >
                    {locked ? <Lock size={16} /> : <LockOpen size={16} />}
                  </button>
                </div>

                {/* Object List (expanded) */}
                {!collapsed && !isEmpty && (() => {
                  const objectList = (
                    <div role="list" aria-label={`Objekte in ${layer.name}`}>
                      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
                        {displayObjects.map((obj) => {
                          if (isUserGroup(obj)) {
                            return (
                              <GroupObjectItem
                                key={obj.__uniqueId}
                                obj={obj}
                                showDragHandle={showDragHandle}
                                isSelected={isObjectSelected(obj)}
                                isHidden={isObjectHidden(obj)}
                                isLocked={isObjectLocked(obj)}
                                onSelect={handleObjectClick}
                                onToggleVisibility={handleToggleObjectHidden}
                                onToggleLock={handleToggleObjectLocked}
                                canvas={canvas}
                                getDisplayName={getObjectDisplayName}
                                isObjectHidden={isObjectHidden}
                                onToggleSubVisibility={handleToggleObjectHidden}
                              />
                            );
                          }
                          const isBackgroundImage = obj.data?.type === 'wizard-background';
                          return (
                            <SortableObjectItem
                              key={obj.__uniqueId}
                              obj={obj}
                              showDragHandle={showDragHandle}
                              isSelected={isObjectSelected(obj)}
                              isHidden={isObjectHidden(obj)}
                              isLocked={isObjectLocked(obj)}
                              displayName={getObjectDisplayName(obj)}
                              onSelect={handleObjectClick}
                              onToggleVisibility={handleToggleObjectHidden}
                              onToggleLock={handleToggleObjectLocked}
                              onRemove={isBackgroundImage ? setBackgroundToRemove : undefined}
                            />
                          );
                        })}
                      </SortableContext>

                      {hasHeightCollection && (
                        <HeightZoneCollectionItem
                          heightObjects={heightObjects}
                          isHidden={areObjectsHidden(heightObjects)}
                          onToggleVisibility={handleToggleObjectsHidden}
                        />
                      )}
                    </div>
                  );

                  if (showDragHandle) {
                    return (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                        onDragEnd={handleDragEnd(objects)}
                      >
                        {objectList}
                      </DndContext>
                    );
                  }

                  return objectList;
                })()}
              </div>
            );
          })}
      </div>

      {/* Confirm removal of the wizard background image (#766). Reversible by
          re-uploading a background via the setup wizard. */}
      <ConfirmDialog
        isOpen={backgroundToRemove !== null}
        onConfirm={confirmRemoveBackground}
        onCancel={() => setBackgroundToRemove(null)}
        title="Hintergrundbild entfernen"
        message="Möchtest du das Hintergrundbild entfernen? Die Garten-Maße bleiben unverändert. Du kannst später über den Assistenten ein neues Bild hochladen."
        confirmLabel="Entfernen"
        cancelLabel="Abbrechen"
        variant="danger"
      />
    </div>
  );
};

export default LayerPanel;
